import {testIdProps} from '@github-ui/test-id-props'
import {memo} from 'react'

import {SystemColumnId} from '../../api/columns/contracts/memex-column'
import {getColumnMeta} from '../../hooks/tanstack-table/helpers/column-api'
import {isAddColumnId} from '../../hooks/tanstack-table/helpers/column-ids'
import type {ColumnInstance} from '../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import type {MemexItemModel} from '../../models/memex-item-model'
import {
  type GroupId,
  isPageTypeForGroupedItems,
  type PageType,
  pageTypeForGroups,
  pageTypeForUngroupedItems,
} from '../../state-providers/memex-items/queries/query-keys'
import {
  useGroupsVisiblePagination,
  useSinglyGroupedItemsVisiblePagination,
  useUngroupedVisiblePagination,
} from '../common/use-visible-pagination'
import {LoadingSubIssuesCell} from './renderers'
import {StyledTableCell} from './table-cell'
import {StyledTableRow} from './table-row'

export const PLACEHOLDER_ROW_COUNT = 5

export const TablePagination: React.FC<{pageType: PageType}> = ({pageType}) => {
  if (pageType === pageTypeForUngroupedItems) {
    return <UngroupedPagination />
  } else if (pageType === pageTypeForGroups) {
    return <GroupsPagination />
  } else if (isPageTypeForGroupedItems(pageType)) {
    return <GroupedItemsPagination groupId={pageType.groupId} />
  } else {
    // The table view doesn't support grouped item batches or secondary groups
    return null
  }
}

const UngroupedPagination: React.FC = () => {
  const {ref, hasNextPage} = useUngroupedVisiblePagination()
  const table = useTableInstance()
  const visibleColumns = table.getVisibleLeafColumns()
  return (
    <div ref={ref} {...testIdProps(`table-pagination`)}>
      {!hasNextPage
        ? null
        : [...Array(PLACEHOLDER_ROW_COUNT).keys()].map(key => <PlaceholderRow key={key} columns={visibleColumns} />)}
    </div>
  )
}

const GroupsPagination: React.FC = () => {
  const {ref, hasNextPage} = useGroupsVisiblePagination()
  const table = useTableInstance()
  const visibleColumns = table.getVisibleLeafColumns()
  return (
    <div ref={ref} {...testIdProps(`table-pagination`)}>
      {!hasNextPage
        ? null
        : [...Array(PLACEHOLDER_ROW_COUNT).keys()].map(key => <PlaceholderRow key={key} columns={visibleColumns} />)}
    </div>
  )
}

const GroupedItemsPagination: React.FC<{groupId: GroupId}> = ({groupId}) => {
  const {ref, hasNextPage} = useSinglyGroupedItemsVisiblePagination(groupId)
  const table = useTableInstance()
  const visibleColumns = table.getVisibleLeafColumns()
  return (
    <div ref={ref} {...testIdProps(`table-pagination-${groupId}`)}>
      {!hasNextPage
        ? null
        : [...Array(PLACEHOLDER_ROW_COUNT).keys()].map(key => <PlaceholderRow key={key} columns={visibleColumns} />)}
    </div>
  )
}

type PlaceholderRowProps = {
  columns: Array<ColumnInstance<MemexItemModel>>
}

const PlaceholderRowUnmemoized: React.FC<PlaceholderRowProps> = ({columns}) => {
  return (
    <StyledTableRow role="row" {...testIdProps('placeholder-row')}>
      {columns.map(column => (
        <StyledTableCell key={column.id} role="gridcell" style={{whiteSpace: 'nowrap', width: column.getSize()}}>
          {getColumnMeta(column)?.Placeholder}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  )
}

export const PlaceholderRow = memo(PlaceholderRowUnmemoized)

export const SubIssuesPlaceholderRow = memo(function SubIssuesPlaceholderRow({
  columns,
  depth = 0,
}: {
  columns: Array<ColumnInstance<MemexItemModel>>
  depth?: number
}) {
  return (
    <StyledTableRow role="row" aria-busy="true" aria-label="Loading sub-issues" {...testIdProps('placeholder-row')}>
      {columns.map(column => (
        <StyledTableCell
          key={column.id}
          role="gridcell"
          style={{whiteSpace: 'nowrap', width: column.getSize(), flexGrow: isAddColumnId(column.id) ? 1 : 'unset'}}
        >
          {column.id === SystemColumnId.Title ? LoadingSubIssuesCell(depth + 1) : getColumnMeta(column)?.Placeholder}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  )
})

try{ TablePagination.displayName ||= 'TablePagination' } catch {}
try{ UngroupedPagination.displayName ||= 'UngroupedPagination' } catch {}
try{ GroupsPagination.displayName ||= 'GroupsPagination' } catch {}
try{ GroupedItemsPagination.displayName ||= 'GroupedItemsPagination' } catch {}
try{ PlaceholderRowUnmemoized.displayName ||= 'PlaceholderRowUnmemoized' } catch {}
try{ PlaceholderRow.displayName ||= 'PlaceholderRow' } catch {}
try{ SubIssuesPlaceholderRow.displayName ||= 'SubIssuesPlaceholderRow' } catch {}